// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/BrandingSection.js

import React, { useState, useEffect, useRef } from 'react';
import { FiSave, FiTrash2, FiUpload, FiImage, FiGlobe, FiEdit3, FiX, FiCheckCircle, FiAlertCircle, FiDroplet, FiInfo, FiType } from 'react-icons/fi';
import ImagePreview from './common/ImagePreview';
import api from '../../api';

// Custom Modal Component
const Modal = ({ isOpen, onClose, title, message, type = 'info', confirmText = 'OK', onConfirm }) => {
  if (!isOpen) return null;

  const bgColors = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    warning: 'bg-yellow-50 border-yellow-200',
    info: 'bg-blue-50 border-blue-200'
  };

  const iconColors = {
    success: 'text-green-500',
    error: 'text-red-500',
    warning: 'text-yellow-500',
    info: 'text-blue-500'
  };

  const Icon = {
    success: FiCheckCircle,
    error: FiAlertCircle,
    warning: FiAlertCircle,
    info: FiCheckCircle
  }[type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className={`relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border ${bgColors[type]}`}>
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className={`p-3 rounded-full ${bgColors[type]} ${iconColors[type]}`}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
          <h3 className="text-lg font-bold text-gray-900 text-center mb-1">{title}</h3>
          <p className="text-gray-600 text-sm text-center mb-6">{message}</p>
          <div className="flex justify-center">
            <button
              onClick={onConfirm || onClose}
              className={`w-full sm:w-auto px-6 py-2.5 rounded-xl text-white font-medium shadow-sm ${
                type === 'success' ? 'bg-green-600 hover:bg-green-700' :
                type === 'error' ? 'bg-red-600 hover:bg-red-700' :
                type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                'bg-blue-600 hover:bg-blue-700'
              } transition-colors duration-200`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = 'Delete', cancelText = 'Cancel' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-red-50 rounded-full">
              <FiAlertCircle className="h-6 w-6 text-red-500" />
            </div>
          </div>
          <h3 className="text-lg font-bold text-gray-900 text-center mb-1">{title}</h3>
          <p className="text-gray-600 text-sm text-center mb-6">{message}</p>
          <div className="flex flex-col-reverse sm:flex-row justify-center gap-3">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-200 font-medium text-sm"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl text-white bg-red-600 hover:bg-red-700 transition-colors duration-200 font-medium text-sm shadow-sm"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notification Toast
const Notification = ({ message, type = 'success', onClose }) => {
  const bgColors = {
    success: 'bg-white border-green-200 text-gray-800 shadow-green-100',
    error: 'bg-white border-red-200 text-gray-800 shadow-red-100',
    warning: 'bg-white border-yellow-200 text-gray-800 shadow-yellow-100',
    info: 'bg-white border-blue-200 text-gray-800 shadow-blue-100'
  };

  const iconColors = {
    success: 'text-green-500 bg-green-50',
    error: 'text-red-500 bg-red-50',
    warning: 'text-yellow-500 bg-yellow-50',
    info: 'text-blue-500 bg-blue-50'
  };

  const Icon = {
    success: FiCheckCircle,
    error: FiAlertCircle,
    warning: FiAlertCircle,
    info: FiCheckCircle
  }[type];

  return (
    <div className="fixed top-4 right-4 left-4 sm:left-auto z-50 max-w-sm">
      <div className={`rounded-xl border p-4 shadow-xl ${bgColors[type]} flex items-start space-x-3`}>
        <div className={`p-1.5 rounded-lg ${iconColors[type]} flex-shrink-0`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 pt-0.5">
          <p className="text-sm font-semibold">{message}</p>
        </div>
        <button onClick={onClose} className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-0.5">
          <FiX className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Replace the FONT_FAMILIES array with this complete list
const FONT_FAMILIES = [
  // Sans-Serif (12 fonts)
  { name: 'Inter', value: 'Inter', category: 'Sans-Serif' },
  { name: 'Montserrat', value: 'Montserrat', category: 'Sans-Serif' },
  { name: 'Roboto', value: 'Roboto', category: 'Sans-Serif' },
  { name: 'Open Sans', value: 'Open Sans', category: 'Sans-Serif' },
  { name: 'Lato', value: 'Lato', category: 'Sans-Serif' },
  { name: 'Poppins', value: 'Poppins', category: 'Sans-Serif' },
  { name: 'Nunito', value: 'Nunito', category: 'Sans-Serif' },
  { name: 'DM Sans', value: 'DM Sans', category: 'Sans-Serif' },
  { name: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans', category: 'Sans-Serif' },
  { name: 'Space Grotesk', value: 'Space Grotesk', category: 'Sans-Serif' },
  { name: 'Raleway', value: 'Raleway', category: 'Sans-Serif' },
  { name: 'Source Sans Pro', value: 'Source Sans Pro', category: 'Sans-Serif' },
  
  // Serif (3 fonts)
  { name: 'Playfair Display', value: 'Playfair Display', category: 'Serif' },
  { name: 'Merriweather', value: 'Merriweather', category: 'Serif' },
  { name: 'Oswald', value: 'Oswald', category: 'Serif' },
  
  // Mono (2 fonts)
  { name: 'JetBrains Mono', value: 'JetBrains Mono', category: 'Mono' },
  { name: 'Fira Code', value: 'Fira Code', category: 'Mono' },
  
  // Display (2 fonts)
  { name: 'Bebas Neue', value: 'Bebas Neue', category: 'Display' },
  { name: 'Anton', value: 'Anton', category: 'Display' }
];

// Preset color combinations - PRIMARY (dark) + SECONDARY (light/bright)
const PRESET_COLORS = [
  {
    name: 'Royal Navy & Gold',
    primary: '#1a1a2e',
    secondary: '#c9a84c',
    description: 'Classic and professional'
  },
  {
    name: 'Dark Blue & Teal',
    primary: '#0f3460',
    secondary: '#0a9396',
    description: 'Calm and trustworthy'
  },
  {
    name: 'Deep Green & Gold',
    primary: '#064e3b',
    secondary: '#b8860b',
    description: 'Rich and prestigious'
  },
  {
    name: 'Dark Crimson & Silver',
    primary: '#8b0000',
    secondary: '#c0c0c0',
    description: 'Bold and sophisticated'
  },
  {
    name: 'Navy & Coral',
    primary: '#1a237e',
    secondary: '#ff6b6b',
    description: 'Modern and vibrant'
  },
  {
    name: 'Forest & Amber',
    primary: '#2d5016',
    secondary: '#ff8c00',
    description: 'Natural and warm'
  },
  {
    name: 'Deep Purple & Gold',
    primary: '#4a148c',
    secondary: '#ffd700',
    description: 'Regal and luxurious'
  },
  {
    name: 'Dark Slate & Sage',
    primary: '#2d3748',
    secondary: '#7caa86',
    description: 'Elegant and calm'
  },
  {
    name: 'Charcoal & Crimson',
    primary: '#1a1a1a',
    secondary: '#dc143c',
    description: 'Modern and powerful'
  },
  {
    name: 'Dark Sapphire & Rose',
    primary: '#0d47a1',
    secondary: '#e91e63',
    description: 'Striking and memorable'
  },
  {
    name: 'Dark Olive & Mustard',
    primary: '#556b2f',
    secondary: '#e5b13b',
    description: 'Earthy and unique'
  },
  {
    name: 'Dark Indigo & Terracotta',
    primary: '#283593',
    secondary: '#d84315',
    description: 'Warm and grounded'
  },
  {
    name: 'Dark & Light Teal',
    primary: '#1a1a2e',
    secondary: '#00b4d8',
    description: 'Contemporary and fresh'
  },
  {
    name: 'Deep Burgundy & Cream',
    primary: '#722f37',
    secondary: '#f5e6d3',
    description: 'Elegant and timeless'
  },
  {
    name: 'Dark Graphite & Lime',
    primary: '#2d2d2d',
    secondary: '#a8d08d',
    description: 'Modern and energetic'
  }
];

const BrandingSection = ({
  websiteData,
  handleInputChange,
  handleSave,
  deleteWebsite,
  tenantId
}) => {
  const [activeField, setActiveField] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slogan: '',
    metaDescription: '',
    primaryColor: '#1a1a2e',
    secondaryColor: '#c9a84c',
    fontFamily: 'Inter'
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [showColorInfo, setShowColorInfo] = useState(false);
  
  // Modal states
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', confirmText: 'OK', onConfirm: null });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [notification, setNotification] = useState(null);
  
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  // Dynamic Google Fonts Loader Hook for Live Preview
  useEffect(() => {
    if (!formData.fontFamily) return;
    
    const fontId = `dynamic-google-font-${formData.fontFamily.replace(/\s+/g, '-').toLowerCase()}`;
    if (document.getElementById(fontId)) return;

    const link = document.createElement('link');
    link.id = fontId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${formData.fontFamily.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`;
    
    document.head.appendChild(link);
  }, [formData.fontFamily]);

  // Initialize form data when websiteData changes
  useEffect(() => {
    if (websiteData?.branding) {
      const branding = websiteData.branding;
      setFormData({
        title: branding.title || '',
        slogan: branding.slogan || '',
        metaDescription: branding.metaDescription || '',
        primaryColor: branding.primaryColor || '#1a1a2e',
        secondaryColor: branding.secondaryColor || '#c9a84c',
        fontFamily: branding.fontFamily || 'Inter'
      });
      setLogoUrl(branding.logo || '');
      setFaviconUrl(branding.favicon || '');
      setHasUnsavedChanges(false);
    }
  }, [websiteData?.branding]);

  // Determine if a website configuration exists
  const hasWebsite = Boolean(websiteData && (websiteData._id || websiteData.createdAt));

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Show modal
  const showModal = (type, title, message, confirmText = 'OK', onConfirm = null) => {
    setModal({ isOpen: true, type, title, message, confirmText, onConfirm });
  };

  // Close modal
  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  // Apply preset color combination
  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary
    }));
    setHasUnsavedChanges(true);
    showNotification(`Applied "${preset.name}" color scheme`, 'success');
  };

  // Local form handlers
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  // Save handler
  const handleSaveClick = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        logo: logoUrl || websiteData?.branding?.logo || '',
        favicon: faviconUrl || logoUrl || websiteData?.branding?.favicon || ''
      };

      handleInputChange('branding', null, payload);
      await handleSave('branding', payload);
      setHasUnsavedChanges(false);
      showNotification('Branding saved successfully!', 'success');
    } catch (error) {
      console.error('Save error:', error);
      showModal('error', 'Save Failed', 'Failed to save branding changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Remove logo handler
  const handleRemoveLogo = async () => {
    const logo = logoUrl || websiteData?.branding?.logo;
    if (!logo) {
      setLogoUrl('');
      setFaviconUrl('');
      handleInputChange('branding', 'logo', '');
      handleInputChange('branding', 'favicon', '');
      setHasUnsavedChanges(true);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Remove Logo',
      message: 'Are you sure you want to remove the logo?',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        
        try {
          let fileKey = null;
          if (typeof logo === 'string') {
            const match = logo.match(/\/api\/files\/(.+)/);
            if (match) {
              fileKey = decodeURIComponent(match[1]);
            }
          }

          if (fileKey && !fileKey.includes('null')) {
            try {
              await api.delete(`/api/files/${encodeURIComponent(fileKey)}`);
            } catch (deleteError) {
              console.warn('Error deleting file from R2:', deleteError);
            }
          }

          setLogoUrl('');
          setFaviconUrl('');
          handleInputChange('branding', 'logo', '');
          handleInputChange('branding', 'favicon', '');
          setHasUnsavedChanges(true);
          showNotification('Logo removed successfully', 'success');
        } catch (error) {
          console.error('Error removing logo:', error);
          showModal('error', 'Error', 'Failed to remove logo. Please try again.');
        }
      }
    });
  };

  // Upload logo using the correct branding endpoint
  const uploadLogo = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !tenantId) return;

    if (file.size > 5 * 1024 * 1024) {
      showModal('error', 'File Too Large', 'File size should be less than 5MB');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      showModal('error', 'Invalid File Type', 'Please upload a valid image file (JPEG, PNG, JPG, GIF, WebP, SVG)');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);

    try {
      const formDataLocal = new FormData();
      formDataLocal.append('logo', file);

      const res = await api.put(
        `/api/website/${tenantId}/branding`,
        formDataLocal,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (res?.data?.success) {
        const { logo, favicon } = res.data.data;

        const newLogoUrl = logo || favicon;
        const newFaviconUrl = favicon || logo;
        
        setLogoUrl(newLogoUrl);
        setFaviconUrl(newFaviconUrl);
        handleInputChange('branding', 'logo', newLogoUrl);
        handleInputChange('branding', 'favicon', newFaviconUrl);
        setHasUnsavedChanges(true);
        
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        showNotification('Logo uploaded successfully! Click "Save Changes" to save.', 'success');
      } else {
        showModal('error', 'Upload Failed', res?.data?.message || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      showModal('error', 'Upload Failed', error?.response?.data?.message || 'Failed to upload logo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Get logo URL for display
  const getLogoUrl = () => {
    let logo = logoUrl || websiteData?.branding?.logo || websiteData?.branding?.favicon;
    
    if (logo && (logo.includes('tenants%2Fnull') || logo.includes('tenants/null'))) {
      logo = websiteData?.branding?.favicon || '';
    }
    
    if (!logo) return '';

    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

    if (typeof logo === 'object') {
      if (logo.url) {
        const url = logo.url.startsWith('/') ? (base ? `${base}${logo.url}` : logo.url) : logo.url;
        return url;
      }
      if (logo.fileId) {
        const url = base ? `${base}/api/files/tenant-website/${logo.fileId}` : `/api/files/tenant-website/${logo.fileId}`;
        return url;
      }
      return '';
    }

    if (/^https?:\/\//i.test(logo)) {
      return logo;
    }
    if (logo.startsWith('/')) {
      return base ? `${base}${logo}` : logo;
    }
    return logo;
  };

  const handleDeleteConfirm = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Website',
      message: 'Are you sure you want to delete all website configuration? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        deleteWebsite();
      }
    });
  };

  // Prevent form submission on enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-3 sm:p-6 pb-24 lg:pb-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Block */}
        <div className="mb-6 bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 bg-blue-50 rounded-xl text-blue-600 flex-shrink-0">
                <FiGlobe className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Brand Identity</h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                  Set up your website logo, company name, colors, and search details.
                </p>
              </div>
            </div>
            
            {/* Desktop Save Action */}
            {hasUnsavedChanges && (
              <div className="hidden lg:flex items-center space-x-3">
                <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
                  Unsaved Changes
                </span>
                <button
                  onClick={handleSaveClick}
                  disabled={saving}
                  className={`inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-xl shadow-sm transition-all ${
                    saving
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <FiSave className="mr-2 h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>

        <form ref={formRef} onKeyPress={handleKeyPress} onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            
            {/* Main Configuration Forms */}
            <div className="lg:col-span-2 xl:col-span-3 space-y-4 sm:space-y-6">
              
              {/* Logo Upload Section */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-50 bg-slate-50/50">
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
                    <FiImage className="mr-2 text-blue-500 h-5 w-5" />
                    Logo & Identity
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">Upload a clean logo image for your platform head.</p>
                </div>
                
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-start">
                    
                    {/* Visual Preview Window */}
                    <div className="sm:col-span-1">
                      <div className="bg-slate-50 rounded-xl p-4 border border-gray-100 flex flex-col items-center">
                        <span className="text-xs font-semibold text-gray-500 mb-3">Current Logo</span>
                        <div className="w-full aspect-video sm:aspect-square bg-white rounded-lg border border-gray-200 shadow-sm flex items-center justify-center p-3 overflow-hidden">
                          {getLogoUrl() ? (
                            <ImagePreview
                              src={getLogoUrl()}
                              alt="Organization logo"
                              className="max-h-full max-w-full object-contain"
                              isLogo={true}
                            />
                          ) : (
                            <div className="text-center text-gray-300">
                              <FiImage className="h-8 w-8 mx-auto mb-1" />
                              <p className="text-xs font-medium">No Logo Uploaded</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Interactive File Inputs */}
                    <div className="sm:col-span-2 space-y-4">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <label className="flex-1 cursor-pointer block">
                          <div className={`border-2 border-dashed rounded-xl p-5 text-center transition-all ${
                            isUploading ? 'border-gray-200 bg-gray-50' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50/50'
                          }`}>
                            <FiUpload className={`h-6 w-6 mx-auto mb-2 ${isUploading ? 'text-gray-300' : 'text-gray-400'}`} />
                            <p className="text-xs font-bold text-gray-700">
                              {isUploading ? 'Uploading file...' : 'Choose Logo File'}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-0.5">PNG, JPG, SVG up to 5MB</p>
                          </div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={uploadLogo}
                            disabled={isUploading}
                            className="hidden"
                          />
                        </label>
                        
                        {getLogoUrl() && (
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            disabled={isUploading}
                            className="sm:w-1/3 py-5 border border-red-200 rounded-xl text-center bg-white hover:bg-red-50 text-red-600 transition-all flex flex-row sm:flex-col items-center justify-center gap-2 text-xs font-bold disabled:opacity-50 px-4"
                          >
                            <FiTrash2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-400" />
                            <span>Remove Logo</span>
                          </button>
                        )}
                      </div>

                      <div className="bg-slate-50 rounded-xl p-3 border border-gray-100 text-[11px] text-gray-500 space-y-1">
                        <p className="font-bold text-gray-700 mb-1">💡 Quick Guidelines:</p>
                        <p>• Transparent PNG logos usually fit background elements best.</p>
                        <p>• Clean squares or standard horizontal rectangles work best.</p>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* Theme Styling, Colors & Fonts */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-50 bg-slate-50/50">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
                      <FiDroplet className="mr-2 text-blue-500 h-5 w-5" />
                      Theme Colors & Fonts
                    </h2>
                    <button
                      type="button"
                      onClick={() => setShowColorInfo(!showColorInfo)}
                      className="text-gray-400 hover:text-blue-600 p-1"
                    >
                      <FiInfo className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">Pick dynamic colors and readable typography layouts.</p>
                </div>
                
                <div className="p-4 sm:p-6">
                  
                  {/* Detailed Color Hint Block */}
                  {showColorInfo && (
                    <div className="mb-5 bg-blue-50/70 border border-blue-100 rounded-xl p-4 text-xs text-blue-900 space-y-3 animate-fadeIn">
                      <p className="font-bold flex items-center gap-1.5"><FiInfo /> Design Setup Assistance</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-white p-2.5 rounded-lg border border-blue-100">
                          <span className="font-bold block text-gray-800 mb-1">Primary Color (Dark theme tones)</span>
                          <span className="text-gray-500">Applied on main text, dark panel headers, and core base panels.</span>
                        </div>
                        <div className="bg-white p-2.5 rounded-lg border border-blue-100">
                          <span className="font-bold block text-gray-800 mb-1">Secondary Color (Light accent tones)</span>
                          <span className="text-gray-500">Applied on highlight links, action tags, and interactive button accents.</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Preset Quick Actions Grid */}
                  <div className="mb-6">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Ready Professional Themes
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2 max-h-48 overflow-y-auto pr-1">
                      {PRESET_COLORS.map((preset, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => applyPreset(preset)}
                          className="p-2 rounded-xl border border-gray-200 bg-white hover:border-blue-500 text-left transition-all shadow-2xs group"
                        >
                          <div className="flex gap-1 mb-1">
                            <div className="w-4 h-4 rounded-full border border-gray-100" style={{ backgroundColor: preset.primary }}></div>
                            <div className="w-4 h-4 rounded-full border border-gray-100" style={{ backgroundColor: preset.secondary }}></div>
                          </div>
                          <p className="text-[11px] font-bold text-gray-700 truncate group-hover:text-blue-600">{preset.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Manual Palette Controls */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-gray-50 pt-5">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-bold text-gray-700">Primary Color (Dark Theme)</label>
                      <div className="flex items-center space-x-2">
                        <div className="relative w-10 h-10 rounded-xl border border-gray-200 overflow-hidden flex-shrink-0 shadow-xs">
                          <input
                            type="color"
                            value={formData.primaryColor}
                            onChange={(e) => handleFieldChange('primaryColor', e.target.value)}
                            className="absolute inset-0 w-[200%] h-[200%] -translate-x-1/4 -translate-y-1/4 cursor-pointer"
                          />
                        </div>
                        <input
                          type="text"
                          value={formData.primaryColor}
                          onChange={(e) => handleFieldChange('primaryColor', e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl bg-slate-50 font-mono focus:bg-white focus:border-blue-500 outline-none transition-all"
                          placeholder="#1a1a2e"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="block text-xs font-bold text-gray-700">Secondary Color (Accent)</label>
                      <div className="flex items-center space-x-2">
                        <div className="relative w-10 h-10 rounded-xl border border-gray-200 overflow-hidden flex-shrink-0 shadow-xs">
                          <input
                            type="color"
                            value={formData.secondaryColor}
                            onChange={(e) => handleFieldChange('secondaryColor', e.target.value)}
                            className="absolute inset-0 w-[200%] h-[200%] -translate-x-1/4 -translate-y-1/4 cursor-pointer"
                          />
                        </div>
                        <input
                          type="text"
                          value={formData.secondaryColor}
                          onChange={(e) => handleFieldChange('secondaryColor', e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl bg-slate-50 font-mono focus:bg-white focus:border-blue-500 outline-none transition-all"
                          placeholder="#c9a84c"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="block text-xs font-bold text-gray-700">Typography Font Family</label>
                      <select
                        value={formData.fontFamily}
                        onChange={(e) => handleFieldChange('fontFamily', e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl bg-slate-50 focus:bg-white focus:border-blue-500 outline-none transition-all h-[42px]"
                      >
                        {Object.entries(
                          FONT_FAMILIES.reduce((acc, current) => {
                            if (!acc[current.category]) acc[current.category] = [];
                            acc[current.category].push(current);
                            return acc;
                          }, {})
                        ).map(([category, fonts]) => (
                          <optgroup label={category} key={category}>
                            {fonts.map((font) => (
                              <option value={font.value} key={font.value}>
                                {font.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Rendering Typography Preview Container */}
                  <div className="mt-5 border border-gray-100 rounded-xl p-4 bg-slate-50/50">
                    <div className="flex items-center space-x-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                      <FiType />
                      <span>Live Font Sample View</span>
                    </div>
                    <div 
                      className="p-4 bg-white rounded-xl border border-gray-100 shadow-2xs transition-all"
                      style={{ fontFamily: `'${formData.fontFamily}', sans-serif` }}
                    >
                      <h3 className="text-xl font-bold mb-0.5" style={{ color: formData.primaryColor }}>
                        {formData.title || 'Main Heading Preview'}
                      </h3>
                      <p className="text-xs font-medium mb-2.5" style={{ color: formData.secondaryColor }}>
                        {formData.slogan || 'Slogan description sample statement text.'}
                      </p>
                      <p className="text-xs text-gray-400 font-mono tracking-tight border-t border-slate-50 pt-2">
                        Aa Bb Cc 123
                      </p>
                    </div>
                  </div>

                </div>
              </div>

              {/* Main Written Text Info Settings */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-50 bg-slate-50/50">
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
                    <FiEdit3 className="mr-2 text-blue-500 h-5 w-5" />
                    Main Details
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">Define your website title text and descriptors.</p>
                </div>
                
                <div className="p-4 sm:p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    
                    {/* Input Title field */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-bold text-gray-700">Website Name *</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        onFocus={() => setActiveField('title')}
                        onBlur={() => setActiveField(null)}
                        className={`w-full px-3 py-2.5 text-sm rounded-xl border transition-all bg-slate-50 outline-none focus:bg-white ${
                          activeField === 'title' ? 'border-blue-500 ring-2 ring-blue-50' : 'border-gray-200'
                        }`}
                        placeholder="e.g. Greenwood Academy"
                      />
                      <p className="text-[11px] text-gray-400">Shows up on top tabs and basic searches.</p>
                    </div>

                    {/* Slogan details text info */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-bold text-gray-700">Slogan / Tagline</label>
                      <input
                        type="text"
                        value={formData.slogan}
                        onChange={(e) => handleFieldChange('slogan', e.target.value)}
                        onFocus={() => setActiveField('slogan')}
                        onBlur={() => setActiveField(null)}
                        className={`w-full px-3 py-2.5 text-sm rounded-xl border transition-all bg-slate-50 outline-none focus:bg-white ${
                          activeField === 'slogan' ? 'border-blue-500 ring-2 ring-blue-50' : 'border-gray-200'
                        }`}
                        placeholder="e.g. Shaping Future Leaders"
                      />
                      <p className="text-[11px] text-gray-400">Short motto statement sentence.</p>
                    </div>
                  </div>

                  {/* Meta Search Details Text area */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700">Search Meta Description *</label>
                    <textarea
                      rows={3}
                      value={formData.metaDescription}
                      onChange={(e) => handleFieldChange('metaDescription', e.target.value)}
                      onFocus={() => setActiveField('metaDescription')}
                      onBlur={() => setActiveField(null)}
                      className={`w-full px-3 py-2.5 text-sm rounded-xl border transition-all bg-slate-50 outline-none focus:bg-white resize-none ${
                        activeField === 'metaDescription' ? 'border-blue-500 ring-2 ring-blue-50' : 'border-gray-200'
                      }`}
                      placeholder="Write a clear text summary that shows up on search listings. Keep it around 150-160 letters."
                    />
                    <div className="flex justify-between items-center text-[11px]">
                      <span className="text-gray-400">Helps users find your website easily.</span>
                      <span className={`font-mono font-bold px-1.5 py-0.5 rounded ${
                        formData.metaDescription.length > 160 ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-gray-600'
                      }`}>
                        {formData.metaDescription.length}/160
                      </span>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Sidebar Columns - Widgets & Desktop Save Block */}
            <div className="space-y-4 sm:space-y-6">
              
              {/* Desktop Widget Card for Save Option */}
              <div className="hidden lg:block bg-white border border-gray-100 rounded-2xl p-5 shadow-sm text-center">
                <div className="p-3 bg-slate-50 rounded-2xl inline-block mb-3">
                  <FiSave className="h-6 w-6 text-slate-700" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">Confirm Configuration</h3>
                <p className="text-xs text-gray-400 mb-4">
                  {hasUnsavedChanges ? "You have modified details to save." : "Everything is up to date."}
                </p>
                <button
                  type="button"
                  onClick={handleSaveClick}
                  disabled={!hasUnsavedChanges || saving}
                  className={`w-full text-xs font-bold py-2.5 px-4 rounded-xl shadow-xs transition-all ${
                    hasUnsavedChanges && !saving
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-slate-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {saving ? 'Processing...' : 'Save Theme Settings'}
                </button>
              </div>

              {/* Visual Mini Mockup Dashboard Layout Branding View */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-50 bg-slate-50/50">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Live Preview Bar</h3>
                </div>
                <div className="p-4">
                  <div 
                    className="rounded-xl p-3.5 text-white shadow-md transition-all duration-300"
                    style={{ backgroundColor: formData.primaryColor }}
                  >
                    <div className="flex items-center space-x-2 mb-2.5">
                      {getLogoUrl() && (
                        <div className="w-5 h-5 bg-white rounded-xs flex items-center justify-center p-0.5 flex-shrink-0">
                          <ImagePreview
                            src={getLogoUrl()}
                            alt="Logo preview icon"
                            className="w-full h-full object-contain"
                            isLogo={true}
                          />
                        </div>
                      )}
                      <span 
                        className="text-xs font-bold truncate block max-w-[150px]"
                        style={{ fontFamily: `'${formData.fontFamily}', sans-serif` }}
                      >
                        {formData.title || 'Brand Title'}
                      </span>
                    </div>
                    
                    <p 
                      className="text-white/70 text-[10px] line-clamp-2 leading-snug"
                      style={{ fontFamily: `'${formData.fontFamily}', sans-serif` }}
                    >
                      {formData.metaDescription || 'Add description details to see view mockup layout here...'}
                    </p>
                    
                    <div className="mt-3 pt-2.5 border-t border-white/10 flex flex-wrap items-center gap-1.5">
                      <span 
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                        style={{ 
                          backgroundColor: formData.secondaryColor,
                          color: '#ffffff'
                        }}
                      >
                        Accent
                      </span>
                      <span className="text-white/50 text-[9px] font-mono">{formData.primaryColor}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Controls / Administrative Action Deletion Block */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-50 bg-slate-50/50">
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Danger Zone</h3>
                </div>
                
                <div className="p-4">
                  {hasWebsite ? (
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={handleDeleteConfirm}
                        className="w-full inline-flex justify-center items-center px-4 py-2.5 text-xs font-bold rounded-xl text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 transition-all"
                      >
                        <FiTrash2 className="mr-1.5 h-4 w-4" />
                        Delete Website Configuration
                      </button>
                      <p className="text-[10px] text-gray-400 text-center">
                        Warning: This will clear all data files and cannot be undone.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-gray-400 text-xs">No active configurations found.</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </form>
      </div>

      {/* Floating Sticky Mobile Bottom Save Menu Option */}
      {hasUnsavedChanges && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40 flex items-center justify-between gap-3 shadow-lg">
          <div className="flex flex-col">
            <span className="text-[11px] font-bold text-amber-700 uppercase">Unsaved Info</span>
            <span className="text-[10px] text-gray-400 truncate max-w-[120px]">Changes made</span>
          </div>
          <button
            type="button"
            onClick={handleSaveClick}
            disabled={saving}
            className="px-5 py-2.5 bg-blue-600 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5"
          >
            <FiSave className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* Modals & Portal Overlays */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        onConfirm={() => {
          if (modal.onConfirm) modal.onConfirm();
          closeModal();
        }}
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      {/* Notification Toast Component */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default BrandingSection;